import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { AlertTriangle, ArrowLeft, BarChart3, Loader2, RefreshCw, Send, XCircle } from 'lucide-react'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { useShowCampaign, useShowCampaignDailyStats, useShowCampaignStats } from '@/shared/hooks/useShows'

type TimeRange = '7d' | '30d' | '90d'

const timeRanges: Array<{ value: TimeRange; label: string }> = [
  { value: '7d', label: '7д' },
  { value: '30d', label: '30д' },
  { value: '90d', label: '90д' },
]

export default function ShowStatistics() {
  const { showId } = useParams<{ showId: string }>()
  const navigate = useNavigate()
  const { hapticFeedback, hapticNotification } = useTelegram()
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')

  useBackButton({ onBack: () => navigate('/shows') })

  useEffect(() => {
    window.scrollTo(0, 0)
    document.body.style.backgroundColor = '#1e2832'
    return () => {
      document.body.style.backgroundColor = ''
    }
  }, [])

  const dateRange = useMemo(() => {
    const end = new Date()
    const start = new Date()
    const days = timeRange === '7d' ? 6 : timeRange === '30d' ? 29 : 89
    start.setDate(start.getDate() - days)
    return {
      start: toDateParam(start),
      end: toDateParam(end),
    }
  }, [timeRange])

  const { data: campaign, isLoading: campaignLoading } = useShowCampaign(showId)
  const { data: totalStats, isLoading: totalLoading, refetch: refetchTotals } = useShowCampaignStats(showId)
  const {
    data: dailyStats,
    isLoading: dailyLoading,
    isFetching,
    error,
    refetch: refetchDaily,
  } = useShowCampaignDailyStats(showId, dateRange.start, dateRange.end)

  const daily = dailyStats?.daily_stats || []
  const rangeTotals = daily.reduce(
    (acc, item) => ({
      sent: acc.sent + item.sent_count,
      skipped: acc.skipped + item.skipped_count,
      failed: acc.failed + item.failed_count,
    }),
    { sent: 0, skipped: 0, failed: 0 }
  )

  const chartData = daily.map((item) => ({
    date: formatShortDate(item.date),
    sent: item.sent_count,
    skipped: item.skipped_count,
    failed: item.failed_count,
  }))

  const maxDailySent = Math.max(1, ...daily.map((item) => item.sent_count))
  const isLoading = campaignLoading || totalLoading || dailyLoading

  const handleRefresh = async () => {
    hapticFeedback('medium')
    try {
      await Promise.all([refetchTotals(), refetchDaily()])
      hapticNotification?.('success')
    } catch (refreshError) {
      console.error('Failed to refresh show statistics:', refreshError)
      hapticNotification?.('error')
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-tg-bg">
        <Loader2 className="h-8 w-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      <div className="border-b border-tg-secondary-bg bg-tg-secondary-bg">
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/shows')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tg-bg text-tg-text active:scale-95"
              aria-label="Назад"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-tg-button/10">
              <BarChart3 className="h-6 w-6 text-tg-button" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-bold text-tg-text">Статистика показа</h1>
              <p className="mt-1 truncate text-sm text-tg-hint">{campaign?.title || showId}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl space-y-3 px-3 py-4">
        <div className="rounded-2xl bg-tg-secondary-bg p-3 shadow-sm">
          <div className="flex gap-1 rounded-xl bg-tg-bg p-1">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => {
                  hapticFeedback('light')
                  setTimeRange(range.value)
                }}
                className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-all ${
                  timeRange === range.value ? 'bg-tg-button text-white shadow-lg' : 'text-tg-hint hover:text-tg-text'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-tg-secondary-bg p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-tg-text">Период</p>
              <p className="mt-1 text-xs text-tg-hint">
                {formatShortDate(dateRange.start)} - {formatShortDate(dateRange.end)}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isFetching}
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-tg-bg text-tg-button disabled:opacity-60"
              aria-label="Обновить"
            >
              <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <SummaryCard icon={<Send className="h-4 w-4" />} label="Отправлено" value={rangeTotals.sent} />
          <SummaryCard icon={<AlertTriangle className="h-4 w-4" />} label="Пропущено" value={rangeTotals.skipped} />
          <SummaryCard icon={<XCircle className="h-4 w-4" />} label="Ошибки" value={rangeTotals.failed} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <SummaryCard label="Всего отправлено" value={totalStats?.sent_count || 0} />
          <SummaryCard label="Резерв" value={totalStats?.reserved_count || 0} />
        </div>

        <div className="rounded-2xl bg-tg-secondary-bg p-3 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-tg-text">По дням</h2>
            <span className="text-xs text-tg-hint">{daily.length} дней</span>
          </div>
          {error ? (
            <div className="rounded-xl bg-tg-bg px-4 py-8 text-center text-sm text-tg-hint">
              Не удалось загрузить статистику
            </div>
          ) : chartData.length === 0 ? (
            <div className="rounded-xl bg-tg-bg px-4 py-8 text-center text-sm text-tg-hint">
              Данных за период пока нет
            </div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 6, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="showSentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5288c1" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#5288c1" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(142, 150, 160, 0.16)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--tg-theme-hint-color)', fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: 'var(--tg-theme-hint-color)', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--tg-theme-secondary-bg-color)',
                      border: '1px solid var(--tg-theme-section-border)',
                      borderRadius: 12,
                      color: 'var(--tg-theme-text-color)',
                    }}
                  />
                  <Area type="monotone" dataKey="sent" name="Отправлено" stroke="#5288c1" fill="url(#showSentGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="space-y-2">
          {daily
            .slice()
            .reverse()
            .map((item) => (
              <div key={item.date} className="rounded-xl bg-tg-secondary-bg p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-tg-text">{formatFullDate(item.date)}</span>
                  <span className="text-sm text-tg-button">{item.sent_count}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-tg-bg">
                  <div className="h-full rounded-full bg-tg-button" style={{ width: `${(item.sent_count / maxDailySent) * 100}%` }} />
                </div>
                <div className="mt-2 flex gap-3 text-xs text-tg-hint">
                  <span>Пропущено: {item.skipped_count}</span>
                  <span>Ошибки: {item.failed_count}</span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

function SummaryCard({ icon, label, value }: { icon?: ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-2xl bg-tg-secondary-bg p-3 shadow-sm">
      <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-tg-bg text-tg-button">
        {icon || <BarChart3 className="h-4 w-4" />}
      </div>
      <div className="text-xl font-semibold text-tg-text">{value}</div>
      <div className="mt-1 text-xs text-tg-hint">{label}</div>
    </div>
  )
}

function toDateParam(date: Date) {
  return date.toISOString().slice(0, 10)
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
  }).format(new Date(value))
}

function formatFullDate(value: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value))
}
